/**
 * Diferença entre as linhas da página e o retrato do polling de status da aba "Meus Estudos"
 * (Flux Personalizado, #2001).
 *
 * Funções puras: sem React, store, fetch nem relógio (instantes entram por parâmetro). O hook
 * `useMyStudiesStatusPolling` decide com elas o que mesclar no cache, o que destacar, o que
 * avisar e quando refazer a página.
 *
 * Mudança = algum campo QUE VEIO no retrato (`MyStudyStatusSnapshot`) diferente do da linha; campo
 * ausente não conta e não é mesclado. Mudar só `n_execucoes` (`executionCount`) CONTA: a linha
 * exibe a contagem ("Resposta hoje, 14:33 · 2 execuções", `.dc.html:640-644`,
 * `formatResponseLine`) e a nota 4 destaca "a linha que muda" (`.dc.html:50`). A contagem só decide
 * o destaque; aviso de pronto e D06 dependem do status.
 */

import { FluxStudyStatusCode, isActiveFluxStudyStatus } from 'constants/fluxStudyStatus';
import { normalizeStudyIds } from 'helper/flux/myStudiesAdapter';
import { isStudyDownloadable } from 'helper/flux/myStudyRow';
import { MyStudy, MyStudyStatusSnapshot } from 'types/Flux/Personalized/myStudies';

/** Campos da linha que o retrato pode trazer (cada um opcional: ausente = mantém o da linha). */
export type MyStudyStatusFields = Omit<MyStudyStatusSnapshot, 'id'>;

const STATUS_FIELDS: readonly (keyof MyStudyStatusFields)[] = [
  'status',
  'statusLabel',
  'lastExecution',
  'lastResponse',
  'executionCount',
  'internalError',
];

/**
 * Aviso de estudo pronto para download (`.dc.html:610`): "<name> está pronto para download."
 * (#2004). `key` = `${id}:${lastResponse}`, a mesma para a mesma conclusão: a fila de avisos
 * deduplica por ela.
 */
export interface StudyReadyEvent {
  key: string;
  id: number;
  name: string;
}

export interface DiffStudiesStatusOptions {
  /** Ids pedidos ao `/estudos/status/` (os ativos da página). */
  requestedIds: readonly number[];
}

export interface StudiesStatusDiff {
  /** Linhas da página cujo status mudou, na ordem do retrato: mesclar e destacar. */
  changedIds: number[];
  /** Transições para pronto baixável, com o nome do estudo. */
  readyEvents: StudyReadyEvent[];
  /** Ids pedidos que não vieram (excluídos ou sem acesso, README do mock): refazer a página. */
  missingIds: number[];
}

/**
 * Os campos de status que VIERAM no retrato, sem o id, prontos para sobrescrever a linha.
 *
 * @param snapshot - Retrato de um estudo (campos ausentes ficam de fora).
 * @returns Só as chaves com valor definido.
 */
export function toStudyStatusFields(snapshot: MyStudyStatusSnapshot): MyStudyStatusFields {
  const fields: Record<string, unknown> = {};
  STATUS_FIELDS.forEach((field) => {
    if (snapshot[field] !== undefined) fields[field] = snapshot[field];
  });
  return fields as MyStudyStatusFields;
}

/**
 * Algum campo que veio no retrato difere do da linha?
 *
 * @param study - Linha da página (ou o rascunho dela no cache).
 * @param snapshot - Retrato do mesmo estudo.
 * @returns `true` se ao menos um campo presente mudou (todos são primitivos).
 */
export function hasStudyStatusChanged(study: MyStudy, snapshot: MyStudyStatusSnapshot): boolean {
  return STATUS_FIELDS.some((field) => (
    snapshot[field] !== undefined && study[field] !== snapshot[field]
  ));
}

/**
 * A linha com os campos do retrato.
 *
 * @returns A mesma linha quando nada mudou; senão uma nova, com os campos ausentes preservados.
 */
export function mergeStudyStatus(study: MyStudy, snapshot: MyStudyStatusSnapshot): MyStudy {
  if (!hasStudyStatusChanged(study, snapshot)) return study;
  return { ...study, ...toStudyStatusFields(snapshot) };
}

/**
 * Ids dos estudos que o polling acompanha: Em Fila, Executando ou status desconhecido
 * (`isActiveFluxStudyStatus`), como o `/estudos/status/` os aceita (`normalizeStudyIds`).
 *
 * @param studies - Linhas da página visível (ou `undefined` sem lista).
 * @returns Ids positivos, sem repetidos, em ordem crescente (a chave canônica do cache de status).
 */
export function activeStudyIds(studies: readonly MyStudy[] | undefined): number[] {
  if (!studies) return [];
  return normalizeStudyIds(
    studies.filter((study) => isActiveFluxStudyStatus(study.status)).map((study) => study.id),
  );
}

/**
 * Regra D06: linhas da página que não satisfazem o filtro de status dela. A lista do servidor
 * sempre satisfaz; só a mescla do polling (ou uma entrada antiga do cache) produz essas linhas.
 *
 * @param rows - Linhas da página.
 * @param statuses - Filtro de status da página (`MyStudiesQuery.statuses`); vazio = todos.
 * @returns Ids fora do filtro, em ordem crescente; vazio sem filtro.
 */
export function studiesOutsideStatusFilter(
  rows: readonly MyStudy[],
  statuses: readonly FluxStudyStatusCode[],
): number[] {
  if (statuses.length === 0) return [];
  return rows
    .filter((row) => row.status === null || !statuses.includes(row.status))
    .map((row) => row.id)
    .sort((a, b) => a - b);
}

/**
 * Compara as linhas da página com o retrato do polling.
 *
 * - Retrato de um id fora da página: ignorado (não há linha para destacar).
 * - Id pedido e ausente da resposta: `missingIds`.
 * - Transição para pronto (de outro status) com o estudo baixável (`isStudyDownloadable`: com
 *   resposta e sem erro interno, a regra do Baixar): 1 aviso. Pronto com erro interno ou sem
 *   resposta só destaca: o aviso diria "pronto para download" com o Baixar desabilitado.
 *
 * @param rows - Linhas da página visível, antes da mescla.
 * @param snapshots - Retrato recebido do `/estudos/status/`.
 * @param options - Ids pedidos.
 * @returns O que mudou e o que fazer com isso (ver {@link StudiesStatusDiff}).
 */
export function diffStudiesStatus(
  rows: readonly MyStudy[],
  snapshots: readonly MyStudyStatusSnapshot[],
  { requestedIds }: DiffStudiesStatusOptions,
): StudiesStatusDiff {
  const rowsById = new Map(rows.map((row) => [row.id, row]));
  const answered = new Set<number>();
  const changedIds: number[] = [];
  const readyEvents: StudyReadyEvent[] = [];

  snapshots.forEach((snapshot) => {
    if (answered.has(snapshot.id)) return;
    answered.add(snapshot.id);

    const row = rowsById.get(snapshot.id);
    if (!row) return;
    const merged = mergeStudyStatus(row, snapshot);
    if (merged === row) return;

    changedIds.push(row.id);
    if (merged.status === 'pronto' && row.status !== 'pronto' && isStudyDownloadable(merged)) {
      readyEvents.push({ key: `${row.id}:${merged.lastResponse}`, id: row.id, name: row.name });
    }
  });

  return {
    changedIds,
    readyEvents,
    missingIds: normalizeStudyIds(requestedIds).filter((id) => !answered.has(id)),
  };
}

/* ------------------------------------------------------------------------------------ */
/* Destaques: os changedIds expiram em ROW_HIGHLIGHT_MS                                 */
/* ------------------------------------------------------------------------------------ */

/** Destaques em curso: id do estudo → instante (ms) em que o destaque termina. */
export type StudyHighlights = ReadonlyMap<number, number>;

/**
 * Marca (ou renova) o destaque dos ids até `until`.
 *
 * @returns Um mapa novo; o mesmo mapa sem id ou com `until` que não é um instante (NaN, Infinity).
 */
export function addHighlights(
  current: StudyHighlights,
  ids: readonly number[],
  until: number,
): StudyHighlights {
  if (ids.length === 0 || !Number.isFinite(until)) return current;
  const next = new Map(current);
  ids.forEach((id) => next.set(id, until));
  return next;
}

/**
 * Tira os destaques que terminaram até `nowMs` (inclusive).
 *
 * @returns Um mapa novo; o mesmo mapa quando nada expirou ou `nowMs` não é um instante.
 */
export function expireHighlights(current: StudyHighlights, nowMs: number): StudyHighlights {
  if (!Number.isFinite(nowMs)) return current;
  const next = new Map<number, number>();
  current.forEach((until, id) => {
    if (until > nowMs) next.set(id, until);
  });
  return next.size === current.size ? current : next;
}

/**
 * Fim do destaque mais próximo, para agendar a expiração.
 *
 * @returns O menor instante, ou `null` sem destaque.
 */
export function nextHighlightExpiry(current: StudyHighlights): number | null {
  let next: number | null = null;
  current.forEach((until) => {
    if (Number.isFinite(until) && (next === null || until < next)) next = until;
  });
  return next;
}
